import React, { useState, useEffect, useRef } from 'react';
import { FireJetSidebar } from '../index';
import ExportDataModal from '../common/ExportDataModal';
import SwitchClinicModal from '../common/SwitchClinicModal';
import ReportQueueModal from '../common/ReportQueueModal';
import { useResponsive } from '../../hooks/useResponsive';
import { useAppStore, useReportQueueStore } from '../../store';
import { useAuth } from '../../contexts/AuthContext';
import { authAPI } from '../../services';
import ENV from '../../config/environment';

function Layout({ children }) {
  const { sidebarExpanded, setSidebarExpanded, toggleSidebar } = useAppStore();
  const { isMobile, isSmallScreen } = useResponsive();
  const {
    user,
    getOrganizationType,
    getOrganizationCity,
    getProfessionalId,
    activeOrganizationId,
    activeOrganizationName,
    activeOrganizationLogo,
    setActiveOrganizationMeta,
  } = useAuth();

  const [showExportModal, setShowExportModal] = useState(false);
  const [showSwitchClinicModal, setShowSwitchClinicModal] = useState(false);
  const [showReportQueueModal, setShowReportQueueModal] = useState(false);
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const profileMenuRef = useRef(null);
  // Tracks the active org id we last resolved display name/logo for, so we can
  // re-resolve when the active clinic changes and avoid redundant fetches.
  const resolvedOrgIdRef = useRef(null);

  const reportQueueCount = useReportQueueStore((s) => s.jobs.length);
  const reportQueueActive = useReportQueueStore((s) =>
    s.jobs.some((j) => j.status === 'building')
  );

  const orgType = getOrganizationType();
  const orgCity = getOrganizationCity();

  const userName = (() => {
    if (!user) return '';
    const given = user.given_name || user.first_name || '';
    const family = user.family_name || '';
    if (given && family) return `${given} ${family}`;
    if (given) return given;
    return user.username || user.email || '';
  })();

  const userInitials = (() => {
    if (!user) return '?';
    const given = user.given_name || user.first_name || '';
    const family = user.family_name || '';
    if (given && family) return `${given[0]}${family[0]}`.toUpperCase();
    if (given) return given[0].toUpperCase();
    if (user.username) return user.username[0].toUpperCase();
    return '?';
  })();

  const orgDisplayName = (() => {
    if (!orgCity && !orgType) return null;
    if (orgType?.toLowerCase() === 'government' && orgCity) {
      return `${orgCity} Veterinary Services Office`;
    }
    if (orgCity) return orgCity;
    return orgType || null;
  })();

  // Prefer the actual active clinic name; fall back to the derived org label.
  const clinicDisplayName = activeOrganizationName || orgDisplayName;

  useEffect(() => {
    if (isSmallScreen) {
      setSidebarExpanded(false);
    } else {
      setSidebarExpanded(true);
    }
  }, [isSmallScreen, setSidebarExpanded]);

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isSmallScreen && sidebarExpanded) {
        setSidebarExpanded(false);
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isSmallScreen, sidebarExpanded, setSidebarExpanded]);

  useEffect(() => {
    const handler = (e) => {
      if (profileMenuRef.current && !profileMenuRef.current.contains(e.target)) {
        setShowProfileMenu(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Resolve/verify the active clinic's name/logo against the current active id.
  // Login returns ids only, and a prior session may have left a name/logo for a
  // different clinic. Always confirm the displayed name matches activeOrganizationId
  // so the profile never shows a clinic that differs from the scoped data.
  useEffect(() => {
    const professionalId = getProfessionalId();
    if (!professionalId || !activeOrganizationId) return;

    // Resolve when we have no name yet (fresh login clears stale name/logo), or when
    // the active clinic changed in-session to an id we haven't resolved for (guards
    // against a lingering name/logo that belongs to a different clinic).
    const nameMissing = !activeOrganizationName;
    const idChangedFromResolved =
      resolvedOrgIdRef.current !== null && resolvedOrgIdRef.current !== activeOrganizationId;
    if (!nameMissing && !idChangedFromResolved) {
      // Trust the persisted name for this id and remember it so later changes re-resolve.
      resolvedOrgIdRef.current = activeOrganizationId;
      return;
    }

    let cancelled = false;
    authAPI.getMyOrganizations(professionalId)
      .then((res) => {
        if (cancelled) return;
        const orgs = Array.isArray(res.data) ? res.data : [];
        const active = orgs.find(
          (o) => String(o.organization_id) === String(activeOrganizationId)
        );
        if (active) {
          setActiveOrganizationMeta(active.organization_name, active.logo);
          resolvedOrgIdRef.current = activeOrganizationId;
        }
      })
      .catch((err) => console.error('Failed to resolve active clinic:', err));
    return () => { cancelled = true; };
  }, [activeOrganizationId, activeOrganizationName, getProfessionalId, setActiveOrganizationMeta]);

  const handleDataExport = () => {
    setShowProfileMenu(false);
    setShowExportModal(true);
  };

  const handleSwitchClinic = () => {
    setShowProfileMenu(false);
    setShowSwitchClinicModal(true);
  };

  const handleReportQueue = () => {
    setShowProfileMenu(false);
    setShowReportQueueModal(true);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <FireJetSidebar
        isExpanded={sidebarExpanded}
        onToggle={toggleSidebar}
        isMobile={isSmallScreen}
      />

      {isSmallScreen && sidebarExpanded && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40"
          onClick={() => setSidebarExpanded(false)}
        />
      )}

      <div className={`transition-all duration-300 ${
        sidebarExpanded
          ? (isSmallScreen ? 'pl-0' : 'pl-64')
          : (isSmallScreen ? 'pl-0' : 'pl-20')
      }`}>
        {/* Top bar */}
        <div className="sticky top-0 z-30 bg-white border-b border-gray-200 px-4 py-2.5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            {isSmallScreen ? (
              <button
                onClick={() => setSidebarExpanded(true)}
                type="button"
                className="p-2 rounded-lg text-gray-600 hover:text-gray-900 hover:bg-gray-100 transition-colors"
                aria-label="Open menu"
              >
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              </button>
            ) : null}
            {ENV.IS_DEVELOPMENT && (() => {
              const isGov = orgType && orgType.toLowerCase() === 'government';
              return (
                <span
                  className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-semibold ${
                    isGov
                      ? 'border-emerald-300 bg-emerald-50 text-emerald-700'
                      : orgType
                        ? 'border-indigo-300 bg-indigo-50 text-indigo-700'
                        : 'border-gray-300 bg-gray-50 text-gray-500'
                  }`}
                  title="Dev-only: active organization type"
                >
                  <span className="uppercase tracking-wide opacity-60">dev</span>
                  <span>{orgType ? (isGov ? 'Government' : 'Private') : 'unknown'}</span>
                  {orgCity ? <span className="opacity-70">· {orgCity}</span> : null}
                </span>
              );
            })()}
          </div>


          {/* Right side: icons + user avatar */}
          <div className="flex items-center gap-1 sm:gap-2">
            {/* Notification bell */}
            <button className="relative p-2 rounded-full text-gray-500 hover:text-gray-700 hover:bg-gray-100 transition-colors">
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M14.857 17.082a23.848 23.848 0 005.454-1.31A8.967 8.967 0 0118 9.75v-.7V9A6 6 0 006 9v.75a8.967 8.967 0 01-2.312 6.022c1.733.64 3.56 1.085 5.455 1.31m5.714 0a24.255 24.255 0 01-5.714 0m5.714 0a3 3 0 11-5.714 0" />
              </svg>
              <span className="absolute top-1.5 right-1.5 w-2 h-2 bg-red-500 rounded-full ring-2 ring-white" />
            </button>


            {/* User avatar + dropdown */}
            <div className="relative" ref={profileMenuRef}>
              <button
                onClick={() => setShowProfileMenu(prev => !prev)}
                className="flex items-center gap-2 pl-2 pr-1 py-1 rounded-full hover:bg-gray-100 transition-colors"
              >
                <div className="w-9 h-9 rounded-full bg-gradient-to-br from-cyan-500 to-teal-500 flex items-center justify-center text-white text-sm font-bold shadow-sm flex-shrink-0">
                  {user?.image_uri ? (
                    <img
                      src={user.image_uri}
                      alt={userName}
                      className="w-9 h-9 rounded-full object-cover"
                      onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling && (e.target.nextSibling.style.display = 'flex'); }}
                    />
                  ) : null}
                  <span style={{ display: user?.image_uri ? 'none' : 'flex' }}>{userInitials}</span>
                </div>
                <svg className={`h-4 w-4 text-gray-400 transition-transform ${showProfileMenu ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
                </svg>
              </button>

              {showProfileMenu && (
                <div className="absolute right-0 top-full mt-2 w-80 bg-white rounded-xl border border-gray-200 shadow-2xl z-50 overflow-hidden">
                  {/* User info */}
                  <div className="px-5 pt-5 pb-4">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-cyan-500 to-teal-500 flex items-center justify-center text-white text-sm font-bold shadow-sm flex-shrink-0">
                        {user?.image_uri ? (
                          <img src={user.image_uri} alt={userName} className="w-10 h-10 rounded-full object-cover" onError={(e) => { e.target.style.display = 'none'; }} />
                        ) : null}
                        <span style={{ display: user?.image_uri ? 'none' : 'flex' }}>{userInitials}</span>
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-semibold text-gray-900 truncate">{userName}</div>
                      </div>
                    </div>

                    {clinicDisplayName && (
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0 overflow-hidden">
                          {activeOrganizationLogo ? (
                            <img
                              src={activeOrganizationLogo}
                              alt={clinicDisplayName}
                              className="w-8 h-8 rounded-full object-cover"
                              onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling && (e.target.nextSibling.style.display = 'block'); }}
                            />
                          ) : null}
                          <svg style={{ display: activeOrganizationLogo ? 'none' : 'block' }} className="w-4 h-4 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 21h19.5m-18-18v18m10.5-18v18m6-13.5V21M6.75 6.75h.75m-.75 3h.75m-.75 3h.75m3-6h.75m-.75 3h.75m-.75 3h.75M6.75 21v-3.375c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21M3 3h12m-.75 4.5H21m-3.75 3h.008v.008h-.008v-.008zm0 3h.008v.008h-.008v-.008zm0 3h.008v.008h-.008v-.008z" />
                          </svg>
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="text-sm font-medium text-green-700">{clinicDisplayName}</div>
                        </div>
                        <button
                          onClick={handleSwitchClinic}
                          className="p-1 rounded text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors flex-shrink-0"
                          aria-label="Switch clinic"
                          title="Switch clinic"
                        >
                          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M7.5 21L3 16.5m0 0L7.5 12M3 16.5h13.5m0-13.5L21 7.5m0 0L16.5 12M21 7.5H7.5" />
                          </svg>
                        </button>
                      </div>
                    )}
                  </div>

                  <div className="border-t border-gray-100" />

                  {/* Menu items */}
                  <div className="py-1">
                    <button
                      onClick={handleDataExport}
                      className="flex items-center gap-3 w-full px-5 py-3 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                    >
                      <svg className="w-5 h-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M9.594 3.94c.09-.542.56-.94 1.11-.94h2.593c.55 0 1.02.398 1.11.94l.213 1.281c.063.374.313.686.645.87.074.04.147.083.22.127.324.196.72.257 1.075.124l1.217-.456a1.125 1.125 0 011.37.49l1.296 2.247a1.125 1.125 0 01-.26 1.431l-1.003.827c-.293.24-.438.613-.431.992a6.759 6.759 0 010 .255c-.007.378.138.75.43.99l1.005.828c.424.35.534.954.26 1.43l-1.298 2.247a1.125 1.125 0 01-1.369.491l-1.217-.456c-.355-.133-.75-.072-1.076.124a6.57 6.57 0 01-.22.128c-.331.183-.581.495-.644.869l-.213 1.28c-.09.543-.56.941-1.11.941h-2.594c-.55 0-1.02-.398-1.11-.94l-.213-1.281c-.062-.374-.312-.686-.644-.87a6.52 6.52 0 01-.22-.127c-.325-.196-.72-.257-1.076-.124l-1.217.456a1.125 1.125 0 01-1.369-.49l-1.297-2.247a1.125 1.125 0 01.26-1.431l1.004-.827c.292-.24.437-.613.43-.992a6.932 6.932 0 010-.255c.007-.378-.138-.75-.43-.99l-1.004-.828a1.125 1.125 0 01-.26-1.43l1.297-2.247a1.125 1.125 0 011.37-.491l1.216.456c.356.133.751.072 1.076-.124.072-.044.146-.087.22-.128.332-.183.582-.495.644-.869l.214-1.281z" />
                        <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                      <span>Data export</span>
                    </button>

                    <button
                      onClick={handleReportQueue}
                      className="flex items-center gap-3 w-full px-5 py-3 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                    >
                      <svg className="w-5 h-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h3.75M9 15h3.75M9 18h3.75m3 .75H18a2.25 2.25 0 002.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 00-1.123-.08m-5.801 0c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 00.75-.75 2.25 2.25 0 00-.1-.664m-5.8 0A2.251 2.251 0 0113.5 2.25H15c1.012 0 1.867.668 2.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m0 0H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V9.375c0-.621-.504-1.125-1.125-1.125H8.25z" />
                      </svg>
                      <span className="flex-1 text-left">Report queue</span>
                      {reportQueueCount > 0 && (
                        <span
                          className={`inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1.5 rounded-full text-xs font-semibold ${
                            reportQueueActive
                              ? 'bg-cyan-100 text-[#2492B6]'
                              : 'bg-gray-100 text-gray-500'
                          }`}
                        >
                          {reportQueueCount}
                        </span>
                      )}
                    </button>
                  </div>

                </div>
              )}
            </div>
          </div>
        </div>

        {/* Page content. overflow-x-clip contains any card that still refuses to
            shrink, so one wide card can't widen the whole document. Deliberately not
            overflow-x-hidden: that computes overflow-y to auto, which would break the
            sticky top bar and trap the page in a nested scroll container. */}
        <main className={`overflow-x-clip ${isMobile ? 'py-2' : 'py-4 sm:py-6'}`}>
          {/* Owns the page's horizontal gutters: 40px from lg up. Dropped max-w-10xl,
              which was not a real Tailwind class and so never applied a cap. */}
          <div className="w-full px-4 sm:px-6 lg:px-10">
            {children}
          </div>
        </main>
      </div>

      <ExportDataModal
        isOpen={showExportModal}
        onClose={() => setShowExportModal(false)}
      />

      <SwitchClinicModal
        isOpen={showSwitchClinicModal}
        onClose={() => setShowSwitchClinicModal(false)}
      />

      <ReportQueueModal
        isOpen={showReportQueueModal}
        onClose={() => setShowReportQueueModal(false)}
      />
    </div>
  );
}

export default Layout;
